import {
  CircleCheck,
  FolderOpen,
  Calendar,
  ChevronRight,
  Plus,
  DollarSign,
  Clock,
  Settings,
} from "lucide-react";
import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Artist, TabId } from "../data";
import { ContentPanel, MetricCard, Avatar, StatusPill } from "./shared";
import StartProjectModal from "./StartProjectModal";

interface Props {
  activeArtists: number;
  artists: Artist[];
  selectedArtist: Artist;
  setActiveTab: (tab: TabId) => void;
  totalArtists: number;
  totalProjects: number;
  onEditArtistProfile: () => void;
  onOpenProject: (artistId: string, projectId: string) => void;
}

type QueueItem = {
  projectId: string;
  title: string;
  status: string;
  reason: string;
  artistId: string;
  artistName: string;
  fileCount: number;
  updatedAt: number;
  priority: number;
};

function formatUpdated(ts?: number): string {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? "Just now" : `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function QueueList({
  items,
  empty,
  onOpen,
}: {
  items: QueueItem[];
  empty: string;
  onOpen: (item: QueueItem) => void;
}) {
  if (items.length === 0) {
    return <p className="empty-state">{empty}</p>;
  }

  return (
    <div className="admin-queue-list">
      {items.map((item) => (
        <button
          key={item.projectId}
          type="button"
          className="admin-queue-row"
          onClick={() => onOpen(item)}
        >
          <div className="admin-queue-main">
            <strong>{item.title}</strong>
            <small>
              {item.artistName}
              {item.fileCount ? ` · ${item.fileCount} files` : ""}
              {item.updatedAt ? ` · ${formatUpdated(item.updatedAt)}` : ""}
            </small>
            <span className="admin-queue-reason">{item.reason}</span>
          </div>
          <span className="admin-queue-side">
            <StatusPill status={item.status} />
            <ChevronRight className="h-4 w-4" />
          </span>
        </button>
      ))}
    </div>
  );
}

export default function AdminOverview({
  activeArtists,
  artists,
  selectedArtist,
  setActiveTab,
  totalArtists,
  totalProjects,
  onEditArtistProfile,
  onOpenProject,
}: Props) {
  const [showStartProject, setShowStartProject] = useState(false);
  const adminSummary = useQuery(api.queries.getAdminSummary, {});

  const fmt = new Intl.NumberFormat("en-AU", {
    currency: "AUD",
    maximumFractionDigits: 0,
    style: "currency",
  });

  const paymentsThisMonth = adminSummary?.paymentsThisMonthCents ?? 0;
  const activeProjectsCount = adminSummary?.activeProjectsCount ?? 0;
  const awaitingAdminCount = adminSummary?.awaitingAdminCount ?? 0;
  const awaitingArtistCount = adminSummary?.awaitingArtistCount ?? 0;
  const needsYou = (adminSummary?.needsYou ?? []) as QueueItem[];
  const needsArtist = (adminSummary?.needsArtist ?? []) as QueueItem[];
  function openQueueItem(item: QueueItem) {
    onOpenProject(item.artistId, item.projectId);
  }

  return (
    <div className="space-stack">
      <section className="hero-panel">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Studio dashboard</h1>
          <p>What needs you, what needs the artist, and payments.</p>
        </div>
        <div className="hero-actions">
          <button
            className="primary-link"
            onClick={() => setShowStartProject(true)}
            type="button"
          >
            <Plus className="h-4 w-4" />
            Start New Project
          </button>
          <a
            className="primary-link"
            href="https://johnsonboibeats.com/book"
            rel="noreferrer"
            target="_blank"
          >
            <Calendar className="h-4 w-4" />
            Book studio
          </a>
        </div>
      </section>

      <section className="metric-grid">
        <MetricCard
          icon={<FolderOpen className="h-5 w-5" />}
          label="Active projects"
          value={activeProjectsCount.toString()}
        />
        <MetricCard
          icon={<Clock className="h-5 w-5" />}
          label="Needs you"
          value={awaitingAdminCount.toString()}
        />
        <MetricCard
          icon={<CircleCheck className="h-5 w-5" />}
          label="Needs artist"
          value={awaitingArtistCount.toString()}
        />
        <MetricCard
          icon={<DollarSign className="h-5 w-5" />}
          label="Payments this month"
          value={fmt.format(paymentsThisMonth / 100)}
        />
      </section>

      <section className="two-column">
        <ContentPanel title="Needs you" eyebrow="Action queue">
          <QueueList
            empty="Nothing waiting on you right now."
            items={needsYou}
            onOpen={openQueueItem}
          />
        </ContentPanel>

        <ContentPanel title="Needs artist" eyebrow="Waiting">
          <QueueList
            empty="No projects waiting on artist review or payment."
            items={needsArtist}
            onOpen={openQueueItem}
          />
        </ContentPanel>
      </section>

      <section className="two-column">
        <ContentPanel title="Current artist" eyebrow="Selected">
          <div className="artist-summary">
            <span className="avatar">{Avatar(selectedArtist.name)}</span>
            <div>
              <h3>{selectedArtist.name}</h3>
              <p>
                {selectedArtist.projects.length} projects / {selectedArtist.files.length} files /{" "}
                {fmt.format(selectedArtist.balanceCents / 100)} open balance
              </p>
            </div>
          </div>
          <div className="action-row">
            <button onClick={onEditArtistProfile} type="button">
              <Settings className="h-4 w-4" />
              Edit artist profile
            </button>
            <button onClick={() => setActiveTab("files")} type="button">
              <ChevronRight className="h-4 w-4" />
              Open Beats
            </button>
          </div>
        </ContentPanel>

        <ContentPanel title="At a glance" eyebrow="Studio">
          <div className="admin-glance-list">
            <div className="admin-glance-row">
              <span>Artists</span>
              <strong>{totalArtists}</strong>
            </div>
            <div className="admin-glance-row">
              <span>Active artists</span>
              <strong>{activeArtists}</strong>
            </div>
            <div className="admin-glance-row">
              <span>Projects</span>
              <strong>{totalProjects}</strong>
            </div>
          </div>
        </ContentPanel>
      </section>

      {showStartProject ? (
        <StartProjectModal artists={artists} isAdmin={true} onClose={() => setShowStartProject(false)} />
      ) : null}
    </div>
  );
}
